import * as Yup from 'yup';
import { TemplateAssignedRole } from 'redux/schemas/models/mentoring-program-action-item-templates';
import { type KeyboardEvent, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import t from 'react-translate';
import type { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { ACTION_ITEM_DESCRIPTION_MAX_LENGTH } from 'athena/components/mentoring-program/constants';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { RootState } from 'redux/schemas';
import {
  ActionItemRowHandlers,
  NEW_ACTION_ITEM_ID,
  PreAssignedActionItem,
  PreAssignedActionItemFormData,
  buildAssigneeRoleOrder,
} from '../types';

type UseActionItemFormProps = {
  row: PreAssignedActionItem;
  isNewRow: boolean;
  setShowAddRow: ActionItemRowHandlers['setShowAddRow'];
  setEditingId: ActionItemRowHandlers['setEditingId'];
  onSave: ActionItemRowHandlers['onSave'];
};

const validationSchema = Yup.object().shape({
  description: Yup.string().required().max(ACTION_ITEM_DESCRIPTION_MAX_LENGTH),
  assignedRole: Yup.string().nullable().oneOf([
    TemplateAssignedRole.MENTOR,
    TemplateAssignedRole.MENTEE,
    null,
  ]),
  required: Yup.boolean(),
});

const useActionItemForm = ({
  row, isNewRow, setShowAddRow, setEditingId, onSave,
}: UseActionItemFormProps) => {
  const { description, assignedRole, required } = row;
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.ACTION_ITEMS;

  const methods = useForm<PreAssignedActionItemFormData>({
    mode: 'onChange',
    defaultValues: {
      description: isNewRow ? '' : description,
      assignedRole: isNewRow ? null : assignedRole,
      required: isNewRow ? false : required,
    },
    resolver: yupResolver(validationSchema),
  });

  const { watch, setValue, handleSubmit } = methods;
  const formDescription = watch('description');
  const formAssigneeRole = watch('assignedRole') as TemplateAssignedRole | null;
  const formRequired = watch('required');
  const hasUnsavedChanges = isNewRow
    ? !!(formDescription?.trim() || formAssigneeRole)
    : formDescription !== description || formAssigneeRole !== assignedRole || formRequired !== required;

  // Guards against the blur fired by unmounting the edit row on Escape also
  // triggering a commit.
  const isCancellingRef = useRef(false);

  const handleSave = useCallback((formData: PreAssignedActionItemFormData) => {
    onSave(
      { ...formData, description: formData.description.trim() },
      isNewRow ? NEW_ACTION_ITEM_ID : row.id as number,
    );
  }, [onSave, isNewRow, row.id]);

  const commitIfChanged = useCallback(() => {
    if (isCancellingRef.current) return;
    if (hasUnsavedChanges) {
      handleSubmit(handleSave)();
    }
  }, [hasUnsavedChanges, handleSubmit, handleSave]);

  const handleSelectAssigneeRole = useCallback((role: TemplateAssignedRole) => {
    setValue('assignedRole', role);
    handleSubmit(handleSave)();
  }, [setValue, handleSubmit, handleSave]);

  const handleRequiredChange = useCallback((checked: boolean) => {
    setValue('required', checked);
    handleSubmit(handleSave)();
  }, [setValue, handleSubmit, handleSave]);

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const aliases = useSelector((state: RootState) => getMentoringProgramAliases(state, mentoringProgram?.id ?? 0));
  const mentorsAlias = aliases?.MentorsAlias ?? '';
  const menteesAlias = aliases?.MenteesAlias ?? '';
  const assigneeRoleOrder = buildAssigneeRoleOrder(mentorsAlias, menteesAlias);

  const editAssigneeOptions: NvDropdownOption[] = useMemo(() => (
    assigneeRoleOrder.map(({ role, text }) => ({
      type: 'text' as const,
      text,
      callback: () => handleSelectAssigneeRole(role),
    }))
  ), [mentorsAlias, menteesAlias, handleSelectAssigneeRole]);

  const editAssigneeTitle = useMemo(
    () => assigneeRoleOrder.find(o => o.role === formAssigneeRole)?.text ?? translationBase.PLACEHOLDER.CHOOSE_ASSIGNEE(),
    [mentorsAlias, menteesAlias, formAssigneeRole, translationBase],
  );

  const editSelectedAssigneeIndex = useMemo(
    () => assigneeRoleOrder.findIndex(o => o.role === formAssigneeRole),
    [mentorsAlias, menteesAlias, formAssigneeRole],
  );

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      isCancellingRef.current = true;
      if (isNewRow) { setShowAddRow(false); } else { setEditingId(null); }
    }
  }, [isNewRow, setShowAddRow, setEditingId]);

  return {
    methods,
    formRequired,
    hasUnsavedChanges,
    editAssigneeOptions,
    editAssigneeTitle,
    editSelectedAssigneeIndex,
    handleKeyDown,
    handleSave,
    commitIfChanged,
    handleRequiredChange,
  };
};

export default useActionItemForm;
